import { ReactElement, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Stack,
  IconButton,
  InputBase,
  InputAdornment,
  CircularProgress,
  Button,
  Avatar,
  Chip,
  SxProps,
  Theme,
} from "@mui/material";
import { Add, Check, KeyboardArrowDown } from "@mui/icons-material";
import { CiSearch } from "react-icons/ci";

import DialogMenu from "../dialog-menu";
import {
  setSelectedClient,
  setSelectedTenant,
  useGetAllTenantsQuery,
} from "@/services";
import { useAppDispatch, useAppSelector } from "@/store";
import { useDebounce } from "@/hooks";
import { useLocation, useNavigate } from "react-router-dom";
import { tokens } from "@/tokens";

interface TenantPickerProps {
  sx?: SxProps<Theme>;
}

const TenantPicker: React.FC<TenantPickerProps> = ({ sx }): ReactElement => {
  const dispatch = useAppDispatch();
  const { tenantManagement: tenants, selectedTenant } = useAppSelector(
    (state) => state.tenantManagement
  );
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [search, setSearch] = useState("");

  const open = useMemo(() => Boolean(anchorEl), [anchorEl]);

  function openClientPickerMenu(event: React.MouseEvent<HTMLButtonElement>) {
    setAnchorEl(event.currentTarget);
  }

  const closeClientPickerMenu = (): void => {
    setAnchorEl(null);
    setSearch("");
  };

  const filterClients = useDebounce(setSearch, 300);

  const { data: tenantsQueryData, isLoading, isFetching } = useGetAllTenantsQuery(
    { searchKeyword: search, pageNo: 1, pageSize: 200 },
    {
      refetchOnMountOrArgChange: true,
      skip: !open,
    }
  );

  const apiTenants = tenantsQueryData?.data ?? [];
  const displayTenants = search.trim()
    ? isFetching && apiTenants.length === 0
      ? tenants
      : apiTenants
    : tenants;

  return (
    <>
      <Button
        id="client-picker-anchor"
        aria-haspopup="true"
        aria-expanded={open ? "true" : undefined}
        aria-controls={open ? "client-picker-menu" : undefined}
        sx={{
          width: { sm: 267 },
          justifyContent: "flex-start",
          padding: {
            xs: 0.5,
            sm: "10px 12px",
          },
          borderRadius: "4px",
          textAlign: "left",
          whiteSpace: "nowrap",
          minWidth: "initial",
          border: { xs: "1px solid transparent", sm: 0 },
          "&[aria-expanded='true']": {
            zIndex: 1500,
            border: {
              xs: `1px solid ${tokens.brand.primary}`,
              sm: 0,
            },
            boxShadow: {
              xs: "0px 0px 0px 4px #0B494B3D",
              sm: "0px 1px 2px 0px rgba(78, 78, 78, 0.05), 0px 0px 0px 4px rgba(11, 73, 75, 0.24)",
            },
          },
          ...sx,
        }}
        onClick={openClientPickerMenu}
      >
        <Stack
          direction="row"
          alignItems="center"
          sx={{
            width: "100%",
            gap: "10px",
            borderRadius: "4px",
            cursor: "pointer",
          }}
        >
          {isLoading ? (
            <CircularProgress size={18} />
          ) : (
            <>
              <Box
                sx={{
                  width: {
                    xs: 30,
                    sm: 34,
                  },
                  height: {
                    xs: 30,
                    sm: 34,
                  },
                  borderRadius: "50%",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  backgroundColor: "#CDD3D3",
                }}
              >
                {selectedTenant ? (
                  <Avatar
                    src={selectedTenant.companyLogo || undefined}
                    sx={{
                      width: "100%",
                      height: "100%",
                    }}
                  />
                ) : (
                  <Add
                    sx={{
                      color: "#0b490b",
                      width: {
                        xs: 18,
                        sm: 24,
                      },
                    }}
                  />
                )}
              </Box>

              <Box
                sx={{
                  flex: 1,
                  overflow: "hidden",
                }}
              >
                <Stack
                  direction="row"
                  alignItems="center"
                  gap={0.5}
                  sx={{
                    color: selectedTenant ? tokens.text.muted : tokens.text.primary,
                    height: selectedTenant ? "14px" : "auto",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: selectedTenant ? tokens.fontSize.base : tokens.fontSize.lg,
                      fontWeight: selectedTenant ? tokens.fontWeight.normal : tokens.fontWeight.semibold,
                    }}
                  >
                    Select tenant
                  </Typography>
                  <KeyboardArrowDown sx={{ color: tokens.text.muted, fontSize: tokens.fontSize.lg }} />
                </Stack>

                {selectedTenant ? (
                  <Typography
                    component="span"
                    sx={{
                      fontSize: tokens.fontSize.lg,
                      fontWeight: tokens.fontWeight.semibold,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "pre",
                    }}
                  >
                    {selectedTenant.companyName}{" "}
                    <Chip
                      sx={{
                        ...(selectedTenant.tenantType === "SERVICE_PROVIDER"
                          ? { bgcolor: tokens.chip.indigo.bg, color: tokens.chip.indigo.text }
                          : { bgcolor: tokens.chip.green.bg, color: tokens.chip.green.text }),
                        fontSize: 8,
                        fontWeight: tokens.fontWeight.semibold,
                        height: 18,
                        borderRadius: tokens.radius.full,
                        ml: 0.4,
                        mb: 0.2,
                      }}
                      label={
                        selectedTenant.tenantType === "SERVICE_PROVIDER"
                          ? "SP"
                          : "Client"
                      }
                      size="small"
                    />
                  </Typography>
                ) : null}
              </Box>
            </>
          )}
        </Stack>
      </Button>

      <DialogMenu
        sx={{
          "& .MuiPaper-root": {
            width: "320px",
            borderRadius: "4px",
            border: 0,
          },
        }}
        id="client-picker-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={closeClientPickerMenu}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <Box sx={{ p: "10px" }}>
          <InputBase
            fullWidth
            sx={{
              backgroundColor: tokens.bg.muted,
              border: `1px solid ${tokens.border.light}`,
              padding: "12px 1rem",
              borderRadius: tokens.radius.md,
              mb: "5px",
              fontSize: tokens.fontSize.lg,

              fontWeight: tokens.fontWeight.semibold,
              lineHeight: "20px",
              "& .MuiInputBase-input": {
                height: "initial",
              },
            }}
            placeholder="Search"
            startAdornment={
              <InputAdornment position="start">
                <CiSearch size={18} />
              </InputAdornment>
            }
            endAdornment={
              isFetching ? (
                <InputAdornment position="end">
                  <CircularProgress size={18} />
                </InputAdornment>
              ) : null
            }
            onChange={(e) => filterClients(e.target.value)}
          />

          {!isFetching && displayTenants.length === 0 ? (
            <Box py="5px" px="10px">
              <Typography
                variant="body2"
                sx={{
    
                }}
              >
                No Tenants Found
              </Typography>
            </Box>
          ) : (
            <Box
              className="client-list"
              sx={{
                maxHeight: 300,
                overflow: "auto",
                "&::-webkit-scrollbar": {
                  width: 3,
                },
                "&::-webkit-scrollbar-thumb": {
                  width: 3,
                  background: tokens.text.disabled,
                  borderRadius: "1px",
                },
              }}
            >
              {displayTenants.map((tenant: ITenant) => (
                <Stack
                  key={tenant._id}
                  direction="row"
                  sx={{
                    padding: "5px 10px",
                    cursor: "pointer",
                    gap: 2,
                    alignItems: "center",
                  }}
                  onClick={() => {
                    if (tenant !== selectedTenant) {
                      dispatch(setSelectedClient(undefined));
                    }
                    if((pathname.includes("clients") || pathname.includes("partners")) && tenant.tenantType === "TRADEDIVE_CLIENT"){
                      navigate(`/dashboard/analytics`);
                    }
                    dispatch(setSelectedTenant(tenant));
                    closeClientPickerMenu();
                  }}
                >
                  <Avatar
                    src={tenant.companyLogo || undefined}
                    children={tenant.companyName?.charAt(0)?.toUpperCase()}
                    sx={{
                      width: 34,
                      height: 34,
                    }}
                  />
                  <Box>
                    <Stack direction="row" alignItems="center" gap={1}>
                      <Typography
                        color={tokens.text.body}
                        fontWeight={tokens.fontWeight.semibold}
                        fontSize={tokens.fontSize.lg}
                      >
                        {tenant.companyName}
                      </Typography>
                      <Chip
                        sx={{
                          ...(tenant.tenantType === "SERVICE_PROVIDER"
                            ? { bgcolor: tokens.chip.indigo.bg, color: tokens.chip.indigo.text }
                            : { bgcolor: tokens.chip.green.bg, color: tokens.chip.green.text }),
                          fontSize: tokens.fontSize["2xs"],
                          fontWeight: tokens.fontWeight.semibold,
                          height: 20,
                          borderRadius: tokens.radius.full,
                        }}
                        label={
                          tenant.tenantType === "SERVICE_PROVIDER" ? "SP" : "Client"
                        }
                        size="small"
                      />
                    </Stack>
                    <Typography color={tokens.text.muted} fontSize={tokens.fontSize.base}>
                      {tenant.companyEmail}
                    </Typography>
                  </Box>

                  {tenant._id === selectedTenant?._id ? (
                    <IconButton
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        width: 22,
                        height: 22,
                        borderRadius: "50%",
                        backgroundColor: tokens.brand.primary,
                        marginLeft: "auto",
                      }}
                    >
                      <Check sx={{ width: 12, height: 12, color: tokens.bg.surface }} />
                    </IconButton>
                  ) : null}
                </Stack>
              ))}
            </Box>
          )}
        </Box>
      </DialogMenu>
    </>
  );
};

export default TenantPicker;
